import { usePrimaryPage } from '@/PageManager'
import ImageWithLightbox from '@/components/ImageWithLightbox'
import { getLongFormArticleMetadataFromEvent } from '@/lib/event-metadata'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useMemo } from 'react'
import Markdown from '@/components/Markdown'

export default function LongFormArticle({
  event,
  className
}: {
  event: Event
  className?: string
}) {
  const { navigate } = usePrimaryPage()
  const metadata = useMemo(() => getLongFormArticleMetadataFromEvent(event), [event])

  // Strip title from content if it appears at the beginning
  const processedContent = useMemo(() => {
    if (!metadata.title) return event.content

    const escapedTitle = metadata.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Match optional whitespace, optional markdown headings (# or ##, etc.), the title, and trailing whitespace/newlines
    const titleRegex = new RegExp(`^\\s*#{0,6}\\s*${escapedTitle}\\s*\n*`, 'i')

    return event.content.replace(titleRegex, '').trimStart()
  }, [event.content, metadata.title])

  return (
    <div
      className={`prose prose-zinc max-w-none dark:prose-invert break-words overflow-wrap-anywhere ${className || ''}`}
    >
      <h1 className="break-words">{metadata.title}</h1>
      {metadata.summary && (
        <blockquote>
          <p className="break-words">{metadata.summary}</p>
        </blockquote>
      )}
      {metadata.image && (
        <ImageWithLightbox
          image={{ url: metadata.image, pubkey: event.pubkey }}
          className="w-full aspect-[3/1] object-cover my-0"
        />
      )}
      <Markdown content={processedContent} event={event} />
      {metadata.tags.length > 0 && (
        <div className="flex gap-2 flex-wrap pb-2">
          {metadata.tags.map((tag) => (
            <div
              key={tag}
              title={tag}
              className="flex items-center rounded-full px-3 bg-muted text-muted-foreground max-w-44 cursor-pointer hover:bg-accent hover:text-accent-foreground"
              onClick={(e) => {
                e.stopPropagation()
                navigate('search', { input: `#t:${tag} kind:${kinds.LongFormArticle}` })
              }}
            >
              #<span className="truncate">{tag}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
